import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, forkJoin, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { SuperAdminManualService } from '../../core/backend/academy/services/super-admin-manual.service';
import { CrmToday } from '../../core/backend/models/crm.model';
import { CrmApiService } from '../../core/backend/services/crm.service';
import { SupportService } from '../../core/backend/services/support.service';
import { AuthenticationService } from '../../core/services/auth.service';
import { NavBadge } from './menu.model';

export type NavCounts = Record<NavBadge, number>;

export const EMPTY_NAV_COUNTS: NavCounts = { renewals: 0, support: 0, approvals: 0, crmAlerts: 0, crmLeads: 0, crmPayments: 0 };

type CrmCounts = Pick<NavCounts, 'crmAlerts' | 'crmLeads' | 'crmPayments'>;

const NO_CRM: CrmCounts = { crmAlerts: 0, crmLeads: 0, crmPayments: 0 };

/** Badge numbers from the CRM home: all alerts, new leads, installments due soon or overdue. */
export function crmCountsFrom(today: CrmToday | null | undefined): CrmCounts {
  const sections = today?.sections;
  if (!sections) {
    return NO_CRM;
  }
  return {
    crmAlerts: today?.alerts || 0,
    crmLeads: sections.new_leads?.count || 0,
    crmPayments: (sections.installments_due?.count || 0) + (sections.installments_overdue?.count || 0),
  };
}

/** Any of these opens the CRM home, whose numbers feed the CRM badges (PF-0142). */
const CRM_HOME_PERMISSIONS = ['super-admin.crm.leads.view', 'super-admin.crm.customers.view', 'super-admin.crm.financial.view'];

/** Minimum gap between two refreshes triggered by navigation. */
const MIN_REFRESH_MS = 30_000;

/**
 * Counts of waiting work for the sidebar badges. Each count is fetched only when the
 * user holds the permission for its page, and a failed request just shows no badge.
 */
@Injectable({ providedIn: 'root' })
export class NavCountsService {
  private readonly countsSubject = new BehaviorSubject<NavCounts>(EMPTY_NAV_COUNTS);
  readonly counts$ = this.countsSubject.asObservable();
  private lastRefresh = 0;

  constructor(
    private readonly superAdmin: SuperAdminManualService,
    private readonly support: SupportService,
    private readonly crm: CrmApiService,
    private readonly auth: AuthenticationService,
  ) {}

  refresh(force = false): void {
    const now = Date.now();
    if (!force && now - this.lastRefresh < MIN_REFRESH_MS) {
      return;
    }
    this.lastRefresh = now;

    forkJoin({
      renewals: this.whenAllowed('super-admin.academies.view', () =>
        this.superAdmin.academySubscriptionSummary().pipe(map((response) => response.data?.subscription?.expiring || 0))),
      support: this.whenAllowed('super-admin.support.view', () =>
        this.support.getViewCounts().pipe(map((counts) => counts?.all_open || 0))),
      approvals: this.whenAllowed('super-admin.custom-messages.view', () =>
        this.superAdmin.listCustomMessages(1, 1, { status: 'pending' }).pipe(map((response) => response.data?.counts?.pending || 0))),
      crm: this.crmCounts(),
    }).subscribe(({ crm, ...counts }) => this.countsSubject.next({ ...counts, ...crm }));
  }

  /** CRM alerts from the CRM home, only while the CRM switch is on. */
  private crmCounts(): Observable<CrmCounts> {
    if (!CRM_HOME_PERMISSIONS.some((permission) => this.auth.hasPermission(permission))) {
      return of(NO_CRM);
    }
    return this.crm.enabled().pipe(
      switchMap((enabled) => (enabled ? this.crm.today() : of(null))),
      map((response) => crmCountsFrom(response?.data)),
      catchError(() => of(NO_CRM)),
    );
  }

  /** The CRM home already has these numbers; it hands them over instead of a second request. */
  updateCrm(today: CrmToday | null | undefined): void {
    this.countsSubject.next({ ...this.countsSubject.value, ...crmCountsFrom(today) });
  }

  private whenAllowed(permission: string, request: () => Observable<number>): Observable<number> {
    if (!this.auth.hasPermission(permission)) {
      return of(0);
    }
    return request().pipe(catchError(() => of(0)));
  }
}
